import "./subway.scss";

import { List } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { withContainerContext } from "../../components/container/ContainerContext";
import { asDropTarget } from "../../components/dragAndDrop/DroppableComponent";
import { withScreenContext } from "../../components/screen/ScreenContext";
import { withScrollPositionContext } from "../../components/scrollable/ScrollPositionContext";
import { SUBWAY_VIEW } from "../../constants/identifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import lineStyles from "../../constants/lineStyles";
import PubSubTopics from "../../constants/pubSubTopics";
import subwayView from "../../constants/styles/subwayView";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { immutableRecords, PropTypeShapes } from "../../types";
import { selectByPubSubTopics } from "../../types/immutableRecords/model/appState/utils";
import { wrapEditingComponent } from "../../utils/codeSplit";
import { isDragMeasuringTimespan, subwayDropRowPreviousStartDate } from "../../utils/dropFunctions";
import rectUtils from "../../utils/rectUtils";
// import withScrollableView from "../../components/scrollable/withScrollableView";
import selectors from "./selectors";
import SubwayAddNodeView from "./SubwayAddNodeView";
import SubwayLine from "./SubwayLine";
import SubwayNodeView from "./SubwayNodeView";

const canDropOnComponent = (props, dragItem, positions) => {
  if (isDragMeasuringTimespan(dragItem)) {
    return false;
  }

  const dropNodes = props.data.nodes;
  let dropDate = null;

  // Work out what thread we are dropping on.
  const hoverThread = getHoverThreadId(positions, props.data.threads);
  const startThread = dragItem.sourceThread;
  let hoverIndex;

  if (hoverThread === startThread) {
    const movingLeft = positions.screenPointerOffset.x < 0;
    hoverIndex = getHoverIndex(positions, props.fontSize, movingLeft);
    dropDate = subwayDropRowPreviousStartDate(dropNodes, hoverIndex, props.displayItems);
  }

  const canDropItems = props.requestCanDropItems(
    dragItem.multipleItemIds,
    dropNodes,
    dropDate,
    hoverThread,
    hoverIndex,
    props.adjustItemEndWhenChangingStart
  );
  return canDropItems.canDrop;
};

const dropCallbacks = {
  onEnter(_props, _dragItem, _dropTarget) {
    // Do nothing
  },

  onLeave(props, dragItem, dropTarget) {
    dropTarget.component.onLeave(props, dragItem, dropTarget);
  },

  onHover(props, dragItem, positions, dropTarget) {
    return dropTarget.component.onHover(props, dragItem, positions, dropTarget);
  },

  onDrop(props, dragItem, positions, dropTarget) {
    const canDrop = canDropOnComponent(props, dragItem, positions);
    if (canDrop) {
      dropTarget.component.onDrop(props, dragItem, positions, dropTarget);
    } else {
      dropTarget.component.cancelDrop();
    }
  },

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    return props.selectedItemIds.contains(dragItem.itemId)
      ? props.selectedItemIds
        .filter(id => props.visibleItemIds.contains(id) || props.data.threads.map(item => item.id).contains(id))
        .toArray()
      : [dragItem.itemId];
  },

  getDragStartParams(props, dragItem, positions, dropTarget) {
    return dropTarget.component.getDragStartParams(props, dragItem, positions, dropTarget);
  },

  getDropPreviewForItem(props, dragItem, dropTarget) {
    return dropTarget.component.getDropPreviewForItem(props, dragItem, dropTarget);
  }
};

// Based on the mouse position, gets the sequence index that is being hovered over.
// Note that this is the index within the filtered list, not the global sequence.
const getHoverIndex = (positions, fontSize, movingLeft) => {
  const mouseAbsX = positions.targetPointerPos.x;
  return selectors.getIndexForXPosition(mouseAbsX, fontSize, movingLeft);
};

// Based on the mouse position, work out which row is being hovered over.
const getHoverThreadId = (positions, threads) => {
  const mouseAbsY = positions.targetPointerPos.y;
  const thread = threads.find(t => t.top <= mouseAbsY && t.bottom > mouseAbsY);
  return thread ? thread.id : null;
};

const getDropChanges = (props, dragItem, positions) => {
  // Variables that will be returned
  let newThread = null;
  let newIndex = null;
  let movingLeft = null;

  // Work out what thread we are dropping on.
  const hoverThread = getHoverThreadId(positions, props.data.threads);
  const startThread = dragItem.sourceThread;

  if (hoverThread !== startThread) {
    newThread = hoverThread;
  } else {
    movingLeft = positions.screenPointerOffset.x < 0;
    const hoverIndex = getHoverIndex(positions, props.fontSize, movingLeft);
    const draggedNode = props.data.nodes.find(n => n.itemId === dragItem.itemId);
    const draggedNodeIndex = draggedNode ? draggedNode.sequenceIndex : null;
    if (draggedNodeIndex !== hoverIndex) {
      newIndex = hoverIndex;
    }

    // Ensure we can't drag beyond the visible events
    const maxIndex = props.visibleItemIds ? props.visibleItemIds.size - 1 : null;
    if (maxIndex != null && newIndex > maxIndex) {
      newIndex = maxIndex;
    }
  }

  return {
    newThread,
    newIndex,
    movingLeft
  };
};

class SubwayNodeCanvasView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.state = {
      drawDropAtX: null,
      highlightDropThread: null
    };
    this.onScrollToItemReceived = this.onScrollToItemReceived.bind(this);
    this.onScrollToDateReceived = this.onScrollToDateReceived.bind(this);
    this.onSelectItemByTopicsReceived = this.onSelectItemByTopicsReceived.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.getDragStartParams = this.getDragStartParams.bind(this);
    this.getDropPreviewForItem = this.getDropPreviewForItem.bind(this);
  }

  componentDidMount() {
    this.props.registerDropTarget(this, dropCallbacks);
    this._pubSubScrollToItemToken = PubSub.subscribe(PubSubTopics.scrollToItem, this.onScrollToItemReceived);
    this._pubSubScrollToDateToken = PubSub.subscribe(PubSubTopics.scrollToDate, this.onScrollToDateReceived);
    this._pubSubSelectItemByTopicsToken = PubSub.subscribe(
      PubSubTopics.selectItemByTopics,
      this.onSelectItemByTopicsReceived
    );
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubScrollToItemToken);
    PubSub.unsubscribe(this._pubSubScrollToDateToken);
    PubSub.unsubscribe(this._pubSubSelectItemByTopicsToken);
  }

  onSelectItemByTopicsReceived(msg, selectTopic) {
    const newSelectedIds = selectByPubSubTopics(
      this.props.isActiveView,
      this.props.visibleItemIds.map(id => {
        return { id: id };
      }),
      selectTopic,
      this.props.selectedItemIds
    );
    if (newSelectedIds.length > 0) {
      this.props.onSelectItems(newSelectedIds);
      this.onScrollToItemReceived("", { id: newSelectedIds[0] });
    }
  }

  onScrollToItemReceived(msg, data) {
    this.scrollToItem(data.id);
  }

  scrollToItem(itemId) {
    const node = this.props.data.nodes.find(n => n.itemId === itemId);
    if (node) {
      const rect = immutableRecords.FlatRectRecord({
        left: node.x,
        right: node.x,
        width: 0,
        top: node.y,
        bottom: node.y,
        height: 0
      });
      this.props.xScrollActions.animateRectIntoView(rect);
      this.props.yScrollActions.animateRectIntoView(rect);
    }
  }

  onScrollToDateReceived(_msg, date) {
    // Scroll to the first node on or after the specified date
    if (date) {
      let lastDatedItem;
      const node = this.props.data.nodes.find(node => {
        const item = this.props.displayItems.find(item => item.id === node.itemId);
        if (isDateValid(item.startDate)) {
          lastDatedItem = item;
          return item.startDate.timestamp >= date.timestamp;
        }
        return false;
      });
      if (node) {
        this.scrollToItem(node.itemId);
        return;
      }
      // If all items are before the specified date, scroll to the last row with a date
      if (lastDatedItem) {
        this.scrollToItem(lastDatedItem.id);
      }
    }
  }

  onLeave(props, _dragItem, _dropTarget) {
    if (props.onInternalDragLeave) {
      props.onInternalDragLeave();
    }
    this.setState({
      drawDropAtX: null,
      highlightDropThread: null
    });
  }

  onHover(props, dragItem, positions, _dropTarget) {
    if (isDragMeasuringTimespan(dragItem)) {
      return {
        message: interfaceMessages.DRAG_DROP_MEASURE_ACROSS_VIEWS_NOT_ALLOWED,
        messageType: "error"
      };
    }

    const dropNodes = props.data.nodes;
    let dropDate = null;

    // Work out what thread we are dropping on.
    const hoverThread = getHoverThreadId(positions, props.data.threads);
    const startThread = dragItem.sourceThread;
    let hoverIndex;

    if (hoverThread === startThread) {
      const movingLeft = positions.screenPointerOffset.x < 0;
      hoverIndex = getHoverIndex(positions, props.fontSize, movingLeft);
      dropDate = subwayDropRowPreviousStartDate(dropNodes, hoverIndex, this.props.displayItems);
    }

    const canDropItems = props.requestCanDropItems(
      dragItem.multipleItemIds,
      dropNodes,
      dropDate,
      hoverThread,
      hoverIndex,
      props.adjustItemEndWhenChangingStart
    );
    const canDrop = canDropItems.canDrop;

    const dropChanges = getDropChanges(props, dragItem, positions);
    const drawDropAtX =
      canDrop && !_.isNil(dropChanges.newIndex)
        ? selectors.getDropPositionForIndex(dropChanges.newIndex, props.fontSize, dropChanges.movingLeft)
        : null;
    const highlightDropThread = canDrop ? dropChanges.newThread : null;
    this.setState({
      drawDropAtX,
      highlightDropThread
    });

    return {
      message: canDropItems.message,
      messageType: canDrop ? "success" : "error"
    };
  }

  onDrop(props, dragItem, positions, _dropTarget) {
    const dropChanges = getDropChanges(props, dragItem, positions);
    const fromExternalSource = this.props.viewId !== dragItem.sourceView;

    if (!_.isNil(dropChanges.newThread) || !_.isNil(dropChanges.newIndex)) {
      this.props.onNodeDrop(
        dragItem.multipleItemIds,
        dropChanges.newThread,
        dropChanges.newIndex,
        dropChanges.movingLeft,
        this.props.adjustItemEndWhenChangingStart,
        fromExternalSource
      );
    }
    this.setState({
      drawDropAtX: null,
      highlightDropThread: null
    });
  }

  cancelDrop() {
    this.setState({
      drawDropAtX: null,
      highlightDropThread: null
    });
  }

  getDropPreviewForItem(_props, dragItem, _dropTarget) {
    let node = this.props.data.nodes.find(item => item.itemId === dragItem.itemId);
    if (!node) {
      return null;
    }

    node = node.set("x", 0);
    node = node.set("y", 0);
    return this.renderNodes(List([node]), [], () => {}, false);
  }

  getDragStartParams(props, dragItem, positions, _dropTarget) {
    const screenPointerPos = _.clone(positions.screenPointerPos);
    const sourceRectPointerPosX = screenPointerPos.x - positions.sourceRect.left;
    const sourceRectPointerPosY = screenPointerPos.y - positions.sourceRect.top;
    return {
      sourceRectPointerPosX,
      sourceRectPointerPosY
    };
    // return {};
  }

  getViewport() {
    const result = immutableRecords.FlatRectRecord({
      left: this.props.xScrollPosition.viewport.min,
      right: this.props.xScrollPosition.viewport.max,
      width: this.props.xScrollPosition.viewport.range,
      top: this.props.yScrollPosition.viewport.min,
      bottom: this.props.yScrollPosition.viewport.max,
      height: this.props.yScrollPosition.viewport.range
    });
    return result;
  }

  renderThreadLines(threads) {
    return threads
      .map(t => {
        const vertices = t.vertices
          .map(v => {
            return {
              x: v.x,
              y: v.y
            };
          })
          .toArray();

        return (
          <SubwayLine
            key={t.id}
            id={`thread-${t.id}`}
            selected={false}
            vertices={vertices}
            color={t.color}
            lineStyle={t.lineStyle}
            strokeWidth={subwayView.LINE_WIDTH}
          />
        );
      })
      .toArray();
  }

  renderThreadAddLines(threads, totalAddIconX) {
    return threads
      .map(t => {
        const vertices = List([t.vertices.last()])
          .push(
            immutableRecords.PointRecord({
              x: t.addIconX,
              y: t.anchorY
            })
          )
          .push(
            immutableRecords.PointRecord({
              x: totalAddIconX,
              y: t.anchorY
            })
          )
          .map(v => {
            return {
              x: v.x,
              y: v.y
            };
          })
          .toArray();
        return (
          <SubwayLine
            key={t.id}
            id={`thread-${t.id}`}
            selected={false}
            vertices={vertices}
            color="iconFade"
            lineStyle={lineStyles.DOTSPACED}
            strokeWidth={2}
          />
        );
      })
      .toArray();
  }

  getRenderedData(data, viewport) {
    // Cut down what we are rendering to just the nodes and parts of threads within
    // the viewport

    // filter out any nodes that are not in our viewport
    const nodes = data.nodes.filter(n => rectUtils.pointOverlapsRect(n, viewport));

    // for each thread, we need to keep any vertices within the X range, unless we want
    // to do further calculations on the y range (since lines may be diagonal etc.)
    // we also need to include the node immediately before and after the range
    const threads = data.threads.map(t => {
      return t.update("vertices", vertices => {
        const firstInside = vertices.findIndex(v => v.x >= viewport.left && v.x <= viewport.right);
        const lastInside = vertices.findLastIndex(v => v.x >= viewport.left && v.x <= viewport.right);
        const keepFrom = Math.max(0, firstInside - 1);
        const keepTo = Math.max(keepFrom + 1, Math.min(vertices.size - 1, lastInside + 1));
        return vertices.slice(keepFrom, keepTo + 1);
      });
    });

    // filter out any nodes that are not in our viewport
    const ticks = data.ticks.filter(t => t.x >= viewport.left && t.x <= viewport.right);

    return data.merge({
      nodes,
      threads,
      ticks
    });
  }

  renderNodes(nodes, selectedIds, onLabelChange, autoEditBlankNodes, isActiveView) {
    let allowDrag = true;
    //#!if iOS === true
// allowDrag = this.props.screenContext.isEditMode;
    //#!endif

    return nodes
      .map(n => {
        const obj = n.toJS();
        const x = obj.x;
        const y = obj.y;

        return (
          <SubwayNodeView
            key={n.nodeId}
            itemId={n.itemId}
            nodeId={n.nodeId}
            {...obj}
            selected={selectedIds.indexOf(n.itemId) >= 0}
            x={x}
            y={y}
            subwayViewSettings={this.props.subwayViewSettings}
            viewId={this.props.viewId}
            onClick={this.props.onItemClick}
            onDoubleClick={this.props.onItemDoubleClick}
            onLabelChange={onLabelChange}
            allowDrag={allowDrag}
            autoEditWhenBlank={autoEditBlankNodes}
            isActiveView={isActiveView}
          />
        );
      })
      .toArray();
  }

  renderAddNodes(threads, totalAddIconX, typesById, defaultType) {
    const defaultTypeLabel = typesById.get(defaultType).label;
    return threads
      .map(t => {
        const x = totalAddIconX;
        const y = t.anchorY;

        return (
          <SubwayAddNodeView
            key={t.id}
            threadId={t.id}
            x={x}
            y={y}
            viewId={this.props.viewId}
            onClick={this.props.onAddNode}
            label={`Create ${defaultTypeLabel}`}
          />
        );
      })
      .toArray();
  }

  renderDropThread(threads, threadId, _viewport) {
    const thread = threads.find(t => t.id === threadId);
    const style = {
      left: 0,
      top: thread.top,
      width: "100%",
      height: thread.bottom - thread.top
    };
    return <div className="subwayThreadDrop" style={style} />;
  }

  renderDropLine(x, viewport) {
    const vertices = [
      {
        x: x,
        y: 0
      },
      {
        x: x,
        y: viewport.height
      }
    ];
    return (
      <SubwayLine
        id={`subwayDropLine`}
        selected={false}
        vertices={vertices}
        color="iconActive"
        lineStyle={lineStyles.THICK}
        strokeWidth={4}
      />
    );
  }

  render() {
    const { data, selectedItemIds, onLabelChange, autoEditBlankNodes, isActiveView } = this.props;

    const viewport = this.getViewport();

    const renderedData = this.getRenderedData(data, viewport);

    return (
      <div className="subwayNodeCanvasView" ref={node => (this._domNode = node)}>
        {this.state.highlightDropThread
          ? this.renderDropThread(renderedData.threads, this.state.highlightDropThread, viewport)
          : null}

        {this.renderThreadLines(renderedData.threads)}
        {wrapEditingComponent(this.renderThreadAddLines(renderedData.threads, renderedData.totalAddIconX))}
        {wrapEditingComponent(
          this.renderAddNodes(
            renderedData.threads,
            renderedData.totalAddIconX,
            this.props.typesById,
            this.props.defaultType
          )
        )}
        {this.renderNodes(renderedData.nodes, selectedItemIds, onLabelChange, autoEditBlankNodes, isActiveView)}
        {!_.isNil(this.state.drawDropAtX) ? this.renderDropLine(this.state.drawDropAtX, viewport) : null}
      </div>
    );
  }
}

SubwayNodeCanvasView.propTypes = {
  fontSize: PropTypes.number.isRequired,
  viewId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  data: PropTypeShapes.subwayData.isRequired,
  allowedThreads: PropTypeShapes.listOptionGroupList.isRequired,
  visibleItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onNodeDrop: PropTypes.func.isRequired,
  // onInternalDragLeave: PropTypes.func.isRequired,
  // onEventDrop: PropTypes.func.isRequired,
  // getConnectorDropOptions: PropTypes.func.isRequired,
  // onRelationshipChange: PropTypes.func.isRequired,
  onThreadToggle: PropTypes.func.isRequired,
  onAddThread: PropTypes.func.isRequired,
  onAddNode: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onItemDoubleClick: PropTypes.func.isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired,
  subwayViewSettings: PropTypeShapes.subwayViewSettings.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,

  requestCanDropItems: PropTypes.func.isRequired,
  adjustItemEndWhenChangingStart: PropTypes.bool.isRequired,
  displayItems: ImmutablePropTypes.list.isRequired,
  typesById: ImmutablePropTypes.map,
  defaultType: PropTypes.string.isRequired,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired,

  // Also accessed by ScrollableView
  requestViewBoundsForZoom: PropTypes.func.isRequired,

  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  xScrollActions: PropTypeShapes.scrollActions.isRequired,
  yScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  yScrollActions: PropTypeShapes.scrollActions.isRequired,

  // From withContainerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

// const scrollableViewOptions = {
//   scrollHorizontally: true,
//   scrollVertically: false,
//   showHorizontalScrollbar: true
// };

// const normalView = withContainerContext(
//   withScrollableView(withScrollPositionContext(asDropTarget(SubwayNodeCanvasView, SUBWAY_VIEW)), scrollableViewOptions)
// );

const normalView = withContainerContext(withScrollPositionContext(asDropTarget(SubwayNodeCanvasView, SUBWAY_VIEW)));

let exportedView = normalView;
//#!if iOS === true
// exportedView = withScreenContext(normalView);
//#!endif

export default exportedView;
